import cacheKeys from '@/data/api/cacheKeys';
import { getProductsBySeller } from '@/data/api/request/productsBySellerRequests';
import { responseToPagingUi } from '@/hooks/pagingMapper';
import { useGetProductsBySeller } from '@/hooks/product/catalog/useGetProductsBySeller';
import { productBySellerResponseToProductByVendorItemUi } from '@/hooks/product/catalog/useGetProductsBySeller/mapper';
import { useTableSearchInPlace } from '@/hooks/useTableSearch';
import { useEffect, useState } from 'react';
import { useImmer } from 'use-immer';
import { initialState, ProductsByVendorUi } from './ProductsByVendorUi';

export function useProductsByVendorViewController() {
  const [uiState, setUiState] = useImmer<ProductsByVendorUi>(initialState);
  const [currentPage, setCurrentPage] = useState(1);

  const { data: tableProductsSellerData, isLoading } =
    useGetProductsBySeller(currentPage);

  const tableSearchHook = useTableSearchInPlace(
    cacheKeys.catalog.sellerProductSearch,
    uiState.searchQuery,
    {
      searchKey: [cacheKeys.catalog.sellerProductList],
      queryFunction: async (searchTerm) => {
        return (await getProductsBySeller({ searchTerm })).data;
      },
      mapper: (response) =>
        response?.map(productBySellerResponseToProductByVendorItemUi) ?? [],
    }
  );

  useEffect(() => {
    setUiState((draft) => {
      draft.isLoading = isLoading;
      if (draft.isLoading) {
        draft.paging = undefined;
        draft.items = [];
      }
    });
  }, [isLoading]);

  const populatePaginatedTable = () => {
    if (!tableProductsSellerData) return;
    setUiState((prev) => {
      prev.paging = responseToPagingUi(tableProductsSellerData);
      prev.items =
        tableProductsSellerData.data?.map(
          productBySellerResponseToProductByVendorItemUi
        ) ?? [];
    });
  };

  useEffect(() => populatePaginatedTable(), [tableProductsSellerData]);

  useEffect(() => {
    setUiState((prev) => {
      if (!tableSearchHook.searchResults) {
        populatePaginatedTable();
        return;
      }

      prev.isSearching = tableSearchHook.isLoading;
      prev.items = tableSearchHook.searchResults ?? [];
      prev.paging = undefined;
    });
  }, [tableSearchHook.searchResults, tableSearchHook.isLoading]);

  const onPageRequested = (page: number) => setCurrentPage(page);

  const onSearchChanged = (value: string) =>
    setUiState((prev) => {
      prev.searchQuery = value;
    });

  return {
    uiState,
    actions: {
      onPageRequested,
      onSearchChanged,
    },
  };
}
